import React, { useRef, useEffect, useState } from 'react';
import Image from 'next/image';
import planck, { Circle, Edge, Vec2 } from 'planck';
import { Card } from '@mui/material';

// --- 改动 1 ---
interface BoxState {
  id: number;
  position: { x: number; y: number; };
  angle: number;
}

// --- 改动 3 ---
const DynamicBoxComponent = ({ position, angle, scale, size }: {
  position: { x: number; y: number };
  angle: number;
  scale: number;
  size: { width: number; height: number };
}) => {
  const style: React.CSSProperties = {
    position: 'absolute',
    width: `${size.width * scale}px`,
    height: `${size.height * scale}px`,
    left: `${position.x * scale - (size.width * scale / 2)}px`,
    top: `${position.y * scale - (size.height * scale / 2)}px`,
    transform: `rotate(${angle * (180 / Math.PI)}deg)`,
    zIndex: 1
  };
  return (
    <div style={style}>
      <Image src={`/tomato-svgrepo-com.svg`} alt={""} width={64} height={64} unoptimized />
    </div>
  );
};

export default function TomatopAnimationBox({ boxCount = 10, children }: { boxCount?: number, children: React.ReactNode }) {
  const world = useRef<planck.World | null>(null);
  const [boxes, setBoxes] = useState<BoxState[]>([]);
  const boxRef = useRef<HTMLDivElement>(null);
  const bodiesRef = useRef<planck.Body[]>([]);
  const scale = 30;
  const dynamicBoxSize = { width: 1, height: 1 }; // SVG是64x64, 64/30 ≈ 2.13

  useEffect(() => {
    world.current = planck.World({ gravity: planck.Vec2(0, 9.8) });
    bodiesRef.current = [];

    if (boxRef.current) {
      const { width, height } = boxRef.current.getBoundingClientRect();
      const worldWidth = width / scale;
      const worldHeight = height / scale;

      const ground = world.current.createBody();
      ground.createFixture(new Edge(new Vec2(0, worldHeight), new Vec2(worldWidth, worldHeight)));
      ground.createFixture(new Edge(new Vec2(0, 0), new Vec2(worldWidth, 0)));
      ground.createFixture(new Edge(new Vec2(0, 0), new Vec2(0, worldHeight)));
      ground.createFixture(new Edge(new Vec2(worldWidth, 0), new Vec2(worldWidth, worldHeight)));

      const initialStates: BoxState[] = [];
      for (let i = 0; i < boxCount; i++) {
        const body = world.current.createDynamicBody({
          position: planck.Vec2(planck.Math.random(0, worldWidth), planck.Math.random(0, worldHeight / 2)),
        });

        const radius = dynamicBoxSize.width / 2.2;
        body.createFixture(new Circle(radius), {
          density: 1.0, friction: 0.5, restitution: 0.2,
        });

        body.setUserData({ id: i });
        bodiesRef.current.push(body);
        initialStates.push({
          id: i,
          position: body.getPosition(),
          angle: body.getAngle(),
        });
      }
      console.log(worldHeight)
      setBoxes(initialStates);

      let frameId: number;
      const step = () => {
        if (world.current && bodiesRef.current.length > 0) {
          world.current.step(1 / 60);

          // --- 改动 2 ---
          const newStates = bodiesRef.current.map(body => ({
            id: (body.getUserData() as { id: number }).id,
            position: body.getPosition(),
            angle: body.getAngle(),
          }));

          setBoxes(newStates);
        }
        frameId = requestAnimationFrame(step);
      };
      step();

      return () => {
        cancelAnimationFrame(frameId);
        world.current = null;
      };
    }
  }, [boxCount, dynamicBoxSize.width]);

  return <>
    <Card ref={boxRef} className="p-4 h-50" sx={{
      position: 'relative',
      overflow: 'hidden',
      border: '1px solid var(--color-border)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      borderRadius: '10px',
      backgroundColor: 'var(--color-statistics-panel-bg)',
      transition: 'background-color 0.3s ease',
    }}>
      <div className='z-10'>
        {children}
      </div>
      <div className='-z-10'>
        {boxes.map(box => (
          <DynamicBoxComponent
            key={box.id}
            position={box.position}
            angle={box.angle}
            scale={scale}
            size={dynamicBoxSize}
          />
        ))}
      </div>
    </Card >
  </>;
};
